import React, { useState, useEffect } from 'react';
import { X, Scale, Gem, Sparkles, Watch } from 'lucide-react';
import { entriesApi, gachasApi, PLACEHOLDER_IMAGE } from '../../lib/api';
import { useLanguage } from '../../context/LanguageContext';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const RARITY_ORDER = ['Uncommon', 'Rare', 'Epic', 'Legendary', 'Mythical', 'Monarch', 'Limited'];

const RARITY_COLORS = {
    Uncommon: { text: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/30' },
    Rare: { text: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/30' },
    Epic: { text: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/30' },
    Legendary: { text: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30' },
    Mythical: { text: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30' },
    Monarch: { text: 'text-pink-400', bg: 'bg-pink-500/10', border: 'border-pink-500/30' },
    Limited: { text: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/30' },
};

// Helper: find the best rarity among items
const getBestRarity = (items) => {
    const rarities = items.map(item => item.rarity).filter(Boolean);
    if (rarities.length === 0) return null;
    return rarities.reduce((best, current) => {
        const bestIndex = RARITY_ORDER.indexOf(best);
        const currentIndex = RARITY_ORDER.indexOf(current);
        return currentIndex > bestIndex ? current : best;
    }, rarities[0]);
};

export const CompareModal = ({ open, onOpenChange }) => {
    const { t } = useLanguage();
    const [itemType, setItemType] = useState('relics');
    const [availableItems, setAvailableItems] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [comparedItems, setComparedItems] = useState([]);
    const [loading, setLoading] = useState(false);

    const ITEM_TYPES = [
        { value: 'relics', label: t('relics'), icon: Gem },
        { value: 'accessories', label: t('accessories'), icon: Watch },
        { value: 'gachas', label: t('gachas'), icon: Sparkles },
    ];

    useEffect(() => {
        if (open) loadAvailableItems();
    }, [open, itemType]);

    const loadAvailableItems = async () => {
        setLoading(true);
        try {
            let response;
            switch (itemType) {
                case 'relics': 
                    response = await entriesApi.getAll('relics', 0, 200); 
                    setAvailableItems(response.data.items || response.data || []); 
                    break;
                case 'accessories': 
                    response = await entriesApi.getAll('accessories', 0, 200); 
                    setAvailableItems(response.data.items || response.data || []); 
                    break;
                case 'gachas': 
                    response = await gachasApi.getAll(null, 0, 200); 
                    const gachaData = response.data.items || response.data || [];
                    setAvailableItems(gachaData.map(g => ({ ...g, title: g.name || g.title }))); 
                    break;
                default: 
                    setAvailableItems([]);
            }
        } catch (error) { 
            console.error('Failed to load items:', error); 
            setAvailableItems([]);
        } finally { 
            setLoading(false); 
        }
    };

    const handleAddItem = (itemId) => {
        if (selectedItems.length >= 4 || selectedItems.includes(itemId)) return;
        setSelectedItems([...selectedItems, itemId]);
    };

    const handleRemoveItem = (itemId) => {
        setSelectedItems(selectedItems.filter(id => id !== itemId));
        setComparedItems(comparedItems.filter(item => item.id !== itemId));
    };

    const handleCompare = () => {
        if (selectedItems.length < 2) return;
        const items = availableItems.filter(item => selectedItems.includes(item.id));
        setComparedItems(items);
    };

    const handleReset = () => { 
        setSelectedItems([]); 
        setComparedItems([]); 
    };

    const selectedItemObjects = availableItems.filter(item => selectedItems.includes(item.id));

    // --- Relics: Collect all unique rank names (using rank_name) ---
    const getAllRankNames = () => {
        const names = new Set();
        comparedItems.forEach(item => {
            (item.ranks || []).forEach(rank => {
                names.add(rank.rank_name || rank.name || '');
            });
        });
        return Array.from(names).filter(Boolean);
    };

    // --- Relics: Collect all unique field names across all ranks ---
    const getAllRankFieldNames = () => {
        const fieldNames = new Set();
        comparedItems.forEach(item => {
            (item.ranks || []).forEach(rank => {
                (rank.fields || []).forEach(field => {
                    fieldNames.add(field.name);
                });
            });
        });
        return Array.from(fieldNames);
    };

    // --- Accessories: Collect all unique attribute names ---
    const getAllAttributeNames = () => {
        const names = new Set();
        comparedItems.forEach(item => {
            (item.attributes || []).forEach(attr => {
                names.add(attr.name);
            });
        });
        return Array.from(names);
    };

    // --- Dynamic Fields (used by all types) ---
    const getAllDynamicFieldKeys = () => {
        const keys = new Set();
        comparedItems.forEach(item => {
            (item.dynamic_fields || []).forEach(field => {
                keys.add(field.name || field.label);
            });
        });
        return Array.from(keys);
    };

    // --- Helper: Best numeric value for a dynamic field ---
    const getBestDynamicFieldValue = (fieldKey) => {
        const values = comparedItems.map(item => {
            const field = (item.dynamic_fields || []).find(f => (f.name || f.label) === fieldKey);
            if (!field) return null;
            const numValue = parseFloat(field.value);
            return isNaN(numValue) ? null : numValue;
        }).filter(v => v !== null);
        return values.length > 0 ? Math.max(...values) : null;
    };

    // --- Helper: Best numeric value for an attribute ---
    const getBestAttributeValue = (attrName) => {
        const values = comparedItems.map(item => {
            const attr = (item.attributes || []).find(a => a.name === attrName);
            if (!attr) return null;
            const numValue = parseFloat(String(attr.value).replace(/[+%]/g, ''));
            return isNaN(numValue) ? null : numValue;
        }).filter(v => v !== null);
        return values.length > 0 ? Math.max(...values) : null;
    };

    // --- Helper: Best numeric value for a rank field across items ---
    const getBestRankFieldValue = (rankName, fieldName) => {
        const values = comparedItems.map(item => {
            const rank = (item.ranks || []).find(r => (r.rank_name || r.name) === rankName);
            if (!rank) return null;
            const field = (rank.fields || []).find(f => f.name === fieldName);
            if (!field) return null;
            const numValue = parseFloat(String(field.value).replace(/[+%]/g, ''));
            return isNaN(numValue) ? null : numValue;
        }).filter(v => v !== null);
        return values.length > 0 ? Math.max(...values) : null;
    };

    const dynamicFieldKeys = comparedItems.length > 0 ? getAllDynamicFieldKeys() : [];
    const rankNames = comparedItems.length > 0 ? getAllRankNames() : [];
    const rankFieldNames = comparedItems.length > 0 ? getAllRankFieldNames() : [];
    const attributeNames = comparedItems.length > 0 ? getAllAttributeNames() : [];
    const bestRarity = comparedItems.length > 0 ? getBestRarity(comparedItems) : null;

    // --- Render: Relics comparison rows ---
    const renderRelicRankRows = () => {
        if (itemType !== 'relics' || rankNames.length === 0) return null;

        return rankNames.map(rankName => {
            // Collect all unique field names for this specific rank
            const fieldsForRank = new Set();
            comparedItems.forEach(item => {
                const rank = (item.ranks || []).find(r => (r.rank_name || r.name) === rankName);
                if (rank) {
                    (rank.fields || []).forEach(f => fieldsForRank.add(f.name));
                }
            });
            const fieldList = Array.from(fieldsForRank);

            return (
                <React.Fragment key={`rank-${rankName}`}>
                    {/* Rank header row */}
                    <tr className="border-b border-white/5 bg-amber-500/5">
                        <td colSpan={comparedItems.length + 1} className="p-3">
                            <span className="text-amber-400 font-semibold text-sm">
                                {t('compareRank')}: {rankName}
                            </span>
                            {/* Show if any item has is_max for this rank */}
                            {comparedItems.some(item => {
                                const rank = (item.ranks || []).find(r => (r.rank_name || r.name) === rankName);
                                return rank?.is_max;
                            }) && (
                                <span className="ml-2 text-xs text-green-400">(MAX)</span>
                            )}
                        </td>
                    </tr>
                    {/* Field rows under this rank */}
                    {fieldList.length > 0 ? fieldList.map(fieldName => {
                        const bestValue = getBestRankFieldValue(rankName, fieldName);
                        return (
                            <tr key={`rank-${rankName}-${fieldName}`} className="border-b border-white/5">
                                <td className="p-3 text-gray-400 text-sm pl-6">? {fieldName}</td>
                                {comparedItems.map(item => {
                                    const rank = (item.ranks || []).find(r => (r.rank_name || r.name) === rankName);
                                    const field = rank ? (rank.fields || []).find(f => f.name === fieldName) : null;
                                    const numValue = field ? parseFloat(String(field.value).replace(/[+%]/g, '')) : null;
                                    const isBest = numValue !== null && !isNaN(numValue) && numValue === bestValue;
                                    
                                    return (
                                        <td key={item.id} className="p-3 text-center">
                                            {field ? (
                                                <span className={cn(
                                                    "font-mono",
                                                    isBest ? "text-green-400 font-bold" : "text-white"
                                                )}>
                                                    {field.value}
                                                    {isBest && <span className="ml-1">?</span>}
                                                </span>
                                            ) : (
                                                <span className="text-gray-600">-</span>
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    }) : (
                        <tr className="border-b border-white/5">
                            <td className="p-3 text-gray-500 text-sm pl-6">? {t('compareNoFields')}</td>
                            {comparedItems.map(item => {
                                const rank = (item.ranks || []).find(r => (r.rank_name || r.name) === rankName);
                                return (
                                    <td key={item.id} className="p-3 text-center">
                                        {rank ? (
                                            <span className="text-cyan-400">?</span>
                                        ) : (
                                            <span className="text-gray-600">-</span>
                                        )}
                                    </td>
                                );
                            })}
                        </tr>
                    )}
                </React.Fragment>
            );
        });
    };

    // --- Render: Accessories attribute rows ---
    const renderAccessoryAttributeRows = () => {
        if (itemType !== 'accessories' || attributeNames.length === 0) return null;

        return (
            <>
                {/* Attributes section header */}
                <tr className="border-b border-white/5 bg-yellow-500/5">
                    <td colSpan={comparedItems.length + 1} className="p-3">
                        <span className="text-yellow-400 font-semibold text-sm">
                            {t('compareAttributes')}
                        </span>
                    </td>
                </tr>
                {attributeNames.map(attrName => {
                    const bestValue = getBestAttributeValue(attrName);
                    return (
                        <tr key={`attr-${attrName}`} className="border-b border-white/5">
                            <td className="p-3 text-gray-400 text-sm pl-6">? {attrName}</td>
                            {comparedItems.map(item => {
                                const attr = (item.attributes || []).find(a => a.name === attrName);
                                const numValue = attr ? parseFloat(String(attr.value).replace(/[+%]/g, '')) : null;
                                const isBest = numValue !== null && !isNaN(numValue) && numValue === bestValue;
                                
                                return (
                                    <td key={item.id} className="p-3 text-center">
                                        {attr ? (
                                            <span className={cn(
                                                "font-mono",
                                                isBest ? "text-green-400 font-bold" : "text-white"
                                            )}>
                                                {attr.value}
                                                {isBest && <span className="ml-1">?</span>}
                                            </span>
                                        ) : (
                                            <span className="text-gray-600">-</span>
                                        )}
                                    </td>
                                );
                            })}
                        </tr>
                    );
                })}
            </>
        );
    };

    // --- Check if there's any comparison data at all ---
    const hasComparisonData = dynamicFieldKeys.length > 0 || 
        (itemType === 'relics' && rankNames.length > 0) || 
        (itemType === 'accessories' && attributeNames.length > 0);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col bg-[#0A0A0A] border-white/10">
                <DialogHeader>
                    <DialogTitle className="text-white font-['Rajdhani'] text-xl flex items-center gap-2">
                        <Scale className="text-cyan-400" /> {t('compareTitle')}
                    </DialogTitle>
                </DialogHeader>
                
                <div className="flex-1 overflow-y-auto space-y-4">
                    {/* Category Selection */}
                    <div className="flex items-center gap-4">
                        <label className="text-sm text-gray-400">{t('compareCategory')}:</label>
                        <Select value={itemType} onValueChange={(v) => { setItemType(v); setSelectedItems([]); setComparedItems([]); }}>
                            <SelectTrigger className="w-48 bg-black/40 border-white/10" data-testid="compare-type-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {ITEM_TYPES.map(type => (
                                    <SelectItem key={type.value} value={type.value}>
                                        <div className="flex items-center gap-2">
                                            <type.icon size={14} />
                                            {type.label}
                                        </div>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Selected Items */}
                    <div className="flex gap-2 flex-wrap">
                        {selectedItemObjects.map(item => (
                            <div key={item.id} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-sm">
                                <span>{item.title || item.name}</span>
                                <button onClick={() => handleRemoveItem(item.id)} className="hover:text-white">
                                    <X size={14} />
                                </button>
                            </div>
                        ))}
                        {selectedItems.length < 4 && (
                            <span className="text-xs text-gray-500 self-center">
                                ({selectedItems.length}/4 {t('compareSelected')})
                            </span>
                        )}
                    </div>

                    {/* Item Selection Grid */}
                    {comparedItems.length === 0 && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-48 overflow-y-auto p-2 rounded-lg bg-black/20">
                            {loading ? (
                                <div className="col-span-full text-center py-4 text-gray-500">{t('loading')}</div>
                            ) : availableItems.length === 0 ? (
                                <div className="col-span-full text-center py-4 text-gray-500">{t('compareNoItems')}</div>
                            ) : (
                                availableItems.map(item => (
                                    <button 
                                        key={item.id} 
                                        onClick={() => handleAddItem(item.id)} 
                                        disabled={selectedItems.includes(item.id) || selectedItems.length >= 4}
                                        className={cn(
                                            "p-2 rounded-lg border text-left transition-colors text-sm",
                                            selectedItems.includes(item.id) 
                                                ? "bg-cyan-500/20 border-cyan-500/50 text-cyan-400" 
                                                : "bg-white/5 border-white/10 hover:border-white/30 text-gray-300 hover:text-white",
                                            selectedItems.length >= 4 && !selectedItems.includes(item.id) && "opacity-50 cursor-not-allowed"
                                        )}
                                    >
                                        <span className="truncate block">{item.title || item.name}</span>
                                    </button>
                                ))
                            )}
                        </div>
                    )}

                    {/* Comparison Table */}
                    {comparedItems.length > 0 && (
                        <div className="overflow-x-auto">
                            <table className="w-full border-collapse">
                                <thead>
                                    <tr>
                                        <th className="text-left p-3 text-gray-400 font-medium text-sm border-b border-white/10 w-40">
                                            {t('compareProperty')}
                                        </th>
                                        {comparedItems.map(item => (
                                            <th key={item.id} className="p-3 border-b border-white/10 min-w-[180px]">
                                                <div className="flex flex-col items-center gap-2">
                                                    <img 
                                                        src={item.image_url || PLACEHOLDER_IMAGE} 
                                                        alt={item.title || item.name} 
                                                        className="w-16 h-16 object-cover rounded-lg"
                                                    />
                                                    <span className="text-white font-medium text-sm text-center">
                                                        {item.title || item.name}
                                                    </span>
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* Rarity Row */}
                                    <tr className="border-b border-white/5">
                                        <td className="p-3 text-gray-400 text-sm">{t('compareRarity')}</td>
                                        {comparedItems.map(item => {
                                            const colors = RARITY_COLORS[item.rarity] || { text: 'text-gray-400', bg: 'bg-gray-500/10', border: 'border-gray-500/30' };
                                            const isBest = item.rarity === bestRarity;
                                            return (
                                                <td key={item.id} className="p-3 text-center">
                                                    {item.rarity ? (
                                                        <span className={cn(
                                                            "px-3 py-1 rounded-full text-sm font-medium border",
                                                            colors.text, colors.bg, colors.border,
                                                            isBest && "ring-2 ring-green-500/50"
                                                        )}>
                                                            {item.rarity}
                                                            {isBest && <span className="ml-1 text-green-400">?</span>}
                                                        </span>
                                                    ) : (
                                                        <span className="text-gray-600">-</span>
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>

                                    {/* Dynamic Fields Rows (shared across all types) */}
                                    {dynamicFieldKeys.map(fieldKey => {
                                        const bestValue = getBestDynamicFieldValue(fieldKey);
                                        return (
                                            <tr key={fieldKey} className="border-b border-white/5">
                                                <td className="p-3 text-gray-400 text-sm">{fieldKey}</td>
                                                {comparedItems.map(item => {
                                                    const field = (item.dynamic_fields || []).find(f => (f.name || f.label) === fieldKey);
                                                    const numValue = field ? parseFloat(field.value) : null;
                                                    const isBest = numValue !== null && !isNaN(numValue) && numValue === bestValue;
                                                    return (
                                                        <td key={item.id} className="p-3 text-center">
                                                            {field ? (
                                                                <span className={cn(
                                                                    "font-mono",
                                                                    isBest ? "text-green-400 font-bold" : "text-white"
                                                                )}>
                                                                    {field.value}
                                                                    {isBest && <span className="ml-1">?</span>}
                                                                </span>
                                                            ) : (
                                                                <span className="text-gray-600">-</span>
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}

                                    {/* Relics: Rank comparison rows */}
                                    {renderRelicRankRows()}

                                    {/* Accessories: Attribute comparison rows */}
                                    {renderAccessoryAttributeRows()}

                                    {/* Fallback if no data */}
                                    {!hasComparisonData && (
                                        <tr>
                                            <td colSpan={comparedItems.length + 1} className="p-4 text-center text-gray-500">
                                                {t('compareNoData')}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Footer Buttons */}
                <div className="flex gap-2 pt-4 border-t border-white/10">
                    {comparedItems.length === 0 ? (
                        <Button 
                            onClick={handleCompare} 
                            disabled={selectedItems.length < 2 || loading} 
                            className="flex-1 bg-cyan-500 hover:bg-cyan-400 text-black" 
                            data-testid="compare-button"
                        >
                            <Scale size={16} className="mr-2" /> 
                            {t('compareAction')} ({selectedItems.length} {t('compareSelected')})
                        </Button>
                    ) : (
                        <Button 
                            onClick={handleReset} 
                            variant="outline" 
                            className="flex-1 border-white/20" 
                            data-testid="reset-compare-button"
                        >
                            {t('compareNew')}
                        </Button>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};